import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import { HARM_TYPES, harmLabel } from "@/lib/constants";
import { Buildings, Lightning, ShieldCheck, CurrencyDollar, Broadcast, ArrowRight } from "@phosphor-icons/react";
import { logger } from "@/lib/logger";

export default function Wins() {
  const [wins, setWins] = useState([]);
  const [stats, setStats] = useState(null);
  const [harmFilter, setHarmFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async (h) => {
    setLoading(true);
    try {
      const params = h ? { harm_type: h } : {};
      const [winsRes, statsRes] = await Promise.all([
        api.get("/public/wins", { params }),
        api.get("/public/wins/stats"),
      ]);
      setWins(winsRes.data);
      setStats(statsRes.data);
    } catch (e) {
      logger.error("Failed to load wins", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(harmFilter); }, [harmFilter]);

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white" data-testid="wins-page">
      <Nav />

      {/* Hero */}
      <div className="border-b border-[#1A1A1A]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="flex items-center gap-3 mb-6">
            <span className="inline-flex items-center gap-2 font-mono-sf text-[10px] uppercase tracking-[0.25em] text-[#00FFCC] border border-[#00FFCC]/40 px-3 py-1.5">
              <span className="w-1.5 h-1.5 bg-[#00FFCC] animate-pulse" />
              PUBLIC RECORD
            </span>
            <span className="font-mono-sf text-[10px] uppercase tracking-wider text-neutral-500">
              // wall-of-friction
            </span>
          </div>
          <h1 className="font-heading font-black uppercase tracking-tighter text-5xl md:text-7xl leading-[0.9] mb-6" data-testid="wins-hero-title">
            Wall of<br />
            <span className="text-[#FF3366]">Friction.</span>
          </h1>
          <p className="max-w-2xl text-neutral-300 text-lg md:text-xl leading-relaxed mb-10">
            Anonymized wins from consumers who fought back and filed. Every entry represents a corporate
            harm that got a paper trail — a demand letter, a CFPB filing, an AG complaint. No names.
            No addresses. Just receipts.
          </p>

          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#1A1A1A] border border-[#1A1A1A]" data-testid="wins-stats-grid">
              {[
                { i: ShieldCheck, l: "Wins Filed", v: stats.total_wins, accent: "#FF3366" },
                { i: Buildings, l: "Corps Hit", v: stats.corporations_hit, accent: "#00FFCC" },
                { i: Broadcast, l: "Filings Sent", v: stats.total_actions, accent: "#FF3366" },
                { i: CurrencyDollar, l: "Damages Claimed", v: `$${Number(stats.total_damages_claimed || 0).toLocaleString()}`, accent: "#00FFCC" },
              ].map((m) => {
                const Ic = m.i;
                return (
                  <div key={m.l} className="bg-[#0D0D0D] p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Ic size={14} weight="bold" style={{ color: m.accent }} />
                      <span className="font-mono-sf text-[10px] uppercase tracking-[0.25em] text-neutral-500">{m.l}</span>
                    </div>
                    <div className="font-heading font-black text-3xl md:text-4xl tracking-tight">{m.v}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Filters + feed */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h2 className="font-heading font-bold uppercase tracking-tight text-2xl">Recent Filings</h2>
            <p className="text-xs text-neutral-500 font-mono-sf mt-1">
              Anonymized · Opt-in only · Names, addresses, and narratives are never shown
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="wins-filter-bar">
            <button
              onClick={() => setHarmFilter("")}
              className={`px-3 h-8 text-[10px] font-mono-sf uppercase tracking-[0.2em] border transition-colors ${!harmFilter ? "bg-[#FF3366] text-white border-[#FF3366]" : "border-[#1A1A1A] hover:border-neutral-600"}`}
              data-testid="wins-filter-all"
            >
              All
            </button>
            {HARM_TYPES.map((h) => (
              <button
                key={h.value}
                onClick={() => setHarmFilter(h.value)}
                className={`px-3 h-8 text-[10px] font-mono-sf uppercase tracking-[0.2em] border transition-colors ${harmFilter === h.value ? "bg-[#FF3366] text-white border-[#FF3366]" : "border-[#1A1A1A] hover:border-neutral-600"}`}
                data-testid={`wins-filter-${h.value}`}
              >
                {h.label.split(" ")[0]}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20 font-mono-sf text-xs uppercase tracking-[0.3em] text-neutral-500" data-testid="wins-loading">Loading feed...</div>
        ) : wins.length === 0 ? (
          <div className="border border-dashed border-[#1A1A1A] p-16 text-center" data-testid="wins-empty">
            <Lightning size={40} weight="bold" className="mx-auto text-[#FF3366] mb-4" />
            <div className="font-heading font-bold uppercase tracking-tight text-xl mb-2">
              No public wins {harmFilter ? "in this category" : "yet"}.
            </div>
            <p className="text-neutral-500 text-sm font-mono-sf mb-6">
              The first receipt goes on the wall soon.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 h-10 px-5 bg-[#FF3366] hover:bg-[#FF5248] text-white text-xs font-mono-sf uppercase tracking-[0.2em] font-bold transition-colors"
              data-testid="wins-cta-register"
            >
              Deploy your first case <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1A1A1A] border border-[#1A1A1A]" data-testid="wins-grid">
            {wins.map((w) => (
              <div key={w.id} className="bg-[#0D0D0D] p-6 flex flex-col" data-testid={`win-card-${w.id}`}>
                <div className="flex items-center justify-between mb-4">
                  <span className="font-mono-sf text-[10px] uppercase tracking-[0.25em] text-[#00FFCC] border border-[#00FFCC]/40 px-2 py-1">
                    FILED
                  </span>
                  <span className="font-mono-sf text-[10px] uppercase tracking-wider text-neutral-600">
                    {w.published_at ? new Date(w.published_at).toLocaleDateString() : ""}
                  </span>
                </div>
                <div className="font-mono-sf text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">
                  vs.
                </div>
                <div className="font-heading font-black uppercase tracking-tight text-2xl mb-3 truncate" title={w.corporation}>
                  {w.corporation}
                </div>
                <div className="text-[10px] font-mono-sf uppercase tracking-[0.2em] text-[#FF3366] mb-4">
                  {harmLabel(w.harm_type)}
                </div>
                {w.share_note && (
                  <p className="text-sm text-neutral-300 font-mono-sf leading-relaxed mb-4 border-l-2 border-[#FF3366] pl-3 italic">
                    "{w.share_note}"
                  </p>
                )}
                <div className="mt-auto pt-4 border-t border-[#1A1A1A] grid grid-cols-3 gap-2 text-center">
                  <div>
                    <div className="font-mono-sf text-[9px] uppercase tracking-[0.2em] text-neutral-500">Damages</div>
                    <div className="font-mono-sf text-xs font-bold text-white mt-1">{w.damages_bucket}</div>
                  </div>
                  <div>
                    <div className="font-mono-sf text-[9px] uppercase tracking-[0.2em] text-neutral-500">Drafts</div>
                    <div className="font-mono-sf text-xs font-bold text-white mt-1">{w.drafts_count}</div>
                  </div>
                  <div>
                    <div className="font-mono-sf text-[9px] uppercase tracking-[0.2em] text-neutral-500">Agencies</div>
                    <div className="font-mono-sf text-xs font-bold text-white mt-1">{w.agencies_count}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CTA footer */}
      <div className="border-t border-[#1A1A1A]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 text-center">
          <h3 className="font-heading font-black uppercase tracking-tighter text-3xl md:text-5xl mb-4">
            Ready to add your <span className="text-[#FF3366]">receipt</span>?
          </h3>
          <p className="text-neutral-400 text-sm font-mono-sf max-w-xl mx-auto mb-8">
            Every filing costs corporations time, money, and regulatory exposure.
            The wall gets bigger every day.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 h-12 px-6 bg-[#FF3366] hover:bg-[#FF5248] text-white text-sm font-mono-sf uppercase tracking-[0.25em] font-bold transition-colors"
            data-testid="wins-footer-cta"
          >
            Deploy <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </div>
    </div>
  );
}
